// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthService } from '../hooks/useAuthService';

// Define the shape of our auth context
interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  isLoading: boolean;
  login: (email: string, password: string, sessionToken?: string) => Promise<any>;
  loginWithProvider: (provider: string) => void;
  logout: () => Promise<boolean>;
  checkAuth: () => Promise<boolean>;
  error: string | null;

}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isAuthenticated: false,
  user: null,
  error: null,
  login: async () => ({ success: false }),
  loginWithProvider: () => {},
  logout: async () => false,
  checkAuth: async () => false,

});


// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Use the auth service through our hook
  const {
    user,
    isAuthenticated,
    isLoading,
    error,
    login,
    loginWithProvider,
    logout,
    checkAuth
  } = useAuthService();

  // In AuthContext.tsx
  useEffect(() => {
    // If login just failed
    if (error) {
      console.log("AUTH ERROR DETECTED - WATCHING FOR REFRESH");
      
      // Add this to detect page unload
      const beforeUnloadHandler = (e) => {
        console.log("PAGE UNLOAD DETECTED");
        e.preventDefault();
        e.returnValue = '';
      };
      
      window.addEventListener('beforeunload', beforeUnloadHandler);
      
      return () => {
        window.removeEventListener('beforeunload', beforeUnloadHandler);
      };
    }
  }, [error]);

  // Provide auth context to children
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        login,
        loginWithProvider,
        logout,
        checkAuth,
        error
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuthContext = () => useContext(AuthContext);



/* // src/contexts/AuthContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';

// Define the shape of our auth context
interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  isLoading: boolean;
  login: (email: string, password: string, sessionToken?: string) => Promise<any>;
  loginWithProvider: (provider: 'google' | 'microsoft' | 'apple') => void;
  logout: () => Promise<boolean>;
  error: string | null;
  checkAuth: () => Promise<boolean>;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isAuthenticated: false,
  user: null,
  error: null,
  login: async () => ({ success: false }),
  loginWithProvider: () => {},
  logout: async () => false,
  checkAuth: async () => false,
});

// Provider component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Get everything from useAuth hook - this is key to fix the issue
  const {
    login,
    loginWithProvider,
    logout,
    checkAuth,
    isLoading,
    error,
    isAuthenticated,
    user,
  } = useAuth();

  // Provide auth context to children
  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        isLoading,
        login,
        loginWithProvider,
        logout,
        error,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuthContext = () => useContext(AuthContext); */